import { tabSelectDropdownStyling } from '@/constants/styles';
import { useLocalization } from '@/hooks/useLocalization';
import {
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  useTheme,
  Menu,
  Box,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { visuallyHidden } from '@mui/utils';
import {
  ReactElement,
  ReactNode,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';

export interface TabItem {
  key: string;
  label: string;
  icon?: ReactElement;
}

/** Synthetic Tabs value for overflow items that only appear in the More menu. */
const MORE_TAB_KEY = 'more' as const;

const tabElementId = (instanceId: string, tabKey: string) =>
  `${instanceId}-tab-${tabKey}`;

const tabPanelId = (instanceId: string, tabKey: string) =>
  `${instanceId}-tabpanel-${tabKey}`;

interface TabPanelProps {
  children?: ReactNode;
  tabKey: string;
  selectedTabKey?: string;
  instanceId: string;
}

// This hook intentionally shares the component module so consumers can keep
// panel state and rendering APIs together.
// eslint-disable-next-line react-refresh/only-export-components
export const useTabsToMobileSelectPanel = () => {
  const instanceId = useId();
  const [selectedTabKey, setSelectedTabKey] = useState<string>('');

  const handleTabChange = (tab: TabItem) => {
    setSelectedTabKey(tab.key);
  };

  return {
    instanceId,
    selectedTabKey,
    handleTabChange,
  };
};

/**
 * Content pane to pair with TabsToMobileSelect. Renders its children
 * whenever `tabKey` matches the currently selected tab's key, so panels
 * stay in sync whether the selection changed by click or programmatically.
 * `instanceId` must be the same value passed to the paired TabsToMobileSelect
 * so the rendered tab and panel reference each other via matching DOM ids.
 * Overflow and Select items have no persistent Tab, so the strip mounts a
 * visually hidden label with the same tab id for this `aria-labelledby`.
 */
export const TabsToMobileSelectPanel = ({
  children,
  tabKey,
  selectedTabKey,
  instanceId,
}: TabPanelProps) => {
  const isSelected = selectedTabKey === tabKey;

  return (
    <div
      role='tabpanel'
      hidden={!isSelected}
      id={tabPanelId(instanceId, tabKey)}
      aria-labelledby={tabElementId(instanceId, tabKey)}
    >
      {isSelected && <Box>{children}</Box>}
    </div>
  );
};

interface TabsToMobileSelectProps {
  /**
   * The full list of tabs to render. If there's one tab or none, nothing is
   * rendered — pair with `TabsToMobileSelectPanel` to just show that single
   * tab's content instead.
   */
  tabs: TabItem[];
  /** Called with the newly selected tab whenever the user picks a `Tab` or `Select` option. */
  handleTabChange: (tab: TabItem) => void;
  /** The `key` of the currently selected tab. When unset, the first tab in `tabs` is auto-selected. */
  selectedTabKey?: string;
  /** How many tabs to show before collapsing the rest into a "More" menu. Defaults to 6. */
  maxTabsVisible?: number;
  /** Unique id shared with the paired `TabsToMobileSelectPanel`(s) to wire matching ARIA ids. */
  instanceId: string;
  /** Accessible name for the tablist / select, e.g. "Terms". */
  ariaLabel?: string;
  forceSelect?: boolean;
}

/**
 * Renders an MUI Tabs strip on medium+ screens (overflow tabs collapse into
 * a "More" menu) and falls back to a Select dropdown on smaller screens.
 * Pair with `TabsToMobileSelectPanel` and `useTabsToMobileSelectPanel` to
 * wire up panel content for the selected tab.
 *
 * Edge cases:
 * - Renders nothing (`null`) when `tabs` has 0 or 1 items — there's nothing
 *   to switch between, so no Tabs/Select UI is shown. With exactly one tab,
 *   it's still auto-selected (see below), so a paired
 *   `TabsToMobileSelectPanel` renders that tab's content as normal.
 * - When `selectedTabKey` is falsy (unset or `''`), the first tab in `tabs`
 *   is auto-selected via `handleTabChange` on mount/update — this is what
 *   lets a freshly-mounted `useTabsToMobileSelectPanel` land on the first
 *   tab without the consumer doing anything. Initialization uses `tabs[0]`
 *   rather than the first visible tab so that `maxTabsVisible={0}` (every
 *   tab in the More menu) still selects a real tab with a paired panel.
 *   This check only looks at whether `selectedTabKey` is falsy, not whether
 *   it matches a real tab: if it's a non-empty key that no longer exists in
 *   `tabs` (e.g. the selected tab was removed), it is *not* auto-corrected
 *   — no `Tab`/option will appear selected until `handleTabChange` is
 *   called again with a valid key.
 * - Only the first `maxTabsVisible` tabs (default 6) render as `Tab`s; the
 *   rest collapse into a "More" menu (passing `0` puts every tab there,
 *   unless `tabs` has 0-1 items, in which case nothing renders at all per
 *   the rule above). If the selected key is one of those overflow items,
 *   the Tabs `value` is mapped to More so MUI still has a matching child
 *   and the strip keeps a selected indicator. More is a menu trigger
 *   (`aria-haspopup`), not a real tab: it never advertises a
 *   `tabpanel-more` id. When an overflow item is selected it points
 *   `aria-controls` at that item's existing panel; while the menu is
 *   open it also points at the menu. Opening the menu is wired to the
 *   More tab's own click handler so it still works after an overflow
 *   selection — MUI Tabs `onChange` does not fire when More is already
 *   selected. Overflow tabs never render
 *   as `Tab`s (and the Select has no tab elements at all), so visually
 *   hidden labels with the matching `${instanceId}-tab-${key}` ids stay
 *   mounted to give paired panels a persistent `aria-labelledby` target.
 *   Tab widths are never measured — the default of 6 assumes they'll fit
 *   on one line at `lg`+,
 *   but the strip itself switches on starting at the narrower `md`
 *   breakpoint, and it no longer scrolls or wraps (no
 *   `variant='scrollable'`) if they don't fit. Pass an explicit
 *   `maxTabsVisible` sized to your labels/container rather than relying on
 *   the default, especially if the strip needs to render below `lg`.
 * - The Tabs/Select split is driven by the `md` breakpoint, not `sm` — the
 *   Select also covers the `sm`–`md` "tablet" gap, not just mobile widths.
 * - `ariaLabel` names the tablist/select for assistive tech. Omitting it
 *   leaves the desktop `Tabs` unlabeled and falls back to a generic
 *   `'Select tab'` label on the Select — pass it whenever this isn't the
 *   only tab-like control on the page.
 *
 * @example
 * ```tsx
 * const { instanceId, selectedTabKey, handleTabChange } = useTabsToMobileSelectPanel();
 *
 * return (
 *   <Box>
 *     <TabsToMobileSelect
 *       tabs={tabs}
 *       selectedTabKey={selectedTabKey}
 *       handleTabChange={handleTabChange}
 *       maxTabsVisible={maxTabsVisible}
 *       instanceId={instanceId}
 *     />
 *
 *     {tabs.map((tab) => (
 *       <TabsToMobileSelectPanel
 *         key={tab.key}
 *         tabKey={tab.key}
 *         selectedTabKey={selectedTabKey}
 *         instanceId={instanceId}
 *       >
 *         <Typography data-testid={`panel-content-${tab.key}`}>
 *           Content for {tab.label}
 *         </Typography>
 *       </TabsToMobileSelectPanel>
 *     ))}
 *   </Box>
 * )
 * ```
 */
export const TabsToMobileSelect = ({
  tabs,
  selectedTabKey,
  maxTabsVisible,
  handleTabChange,
  instanceId,
  ariaLabel,
  forceSelect = false,
}: TabsToMobileSelectProps) => {
  const { t } = useLocalization();
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumViewport = useMediaQuery(theme.breakpoints.up('md'));
  const isMedium = isMediumViewport && !forceSelect;

  const MAX_TABS_VISIBLE = maxTabsVisible ?? 6;

  /**
   * Display up to MAX_TABS_VISIBLE. If there's more tabs,
   * then they get pushed to the "More" menu.
   */
  const visibleTabs = useMemo(() => {
    const visible: TabItem[] = tabs.slice(0, MAX_TABS_VISIBLE);

    if (tabs.length > visible.length) {
      visible.push({
        key: MORE_TAB_KEY,
        label: t('more', 'More'),
        icon: <ArrowDropDownIcon />,
      });
    }

    return visible;
  }, [MAX_TABS_VISIBLE, t, tabs]);

  /**
   * Default to the first real tab when none is preselected. Do not use
   * visibleTabs[0] — when maxTabsVisible is 0 that item is the synthetic
   * "More" tab, which has no paired panel.
   */
  useEffect(() => {
    if (!selectedTabKey && tabs.length) {
      handleTabChange(tabs[0]);
    }
  }, [handleTabChange, selectedTabKey, tabs]);

  /**
   * Items that display under the "More" menu.
   * Instead of calculating tab size, we're assuming that at
   * least six items will fit in lg & up screen sizes. This
   * reduces complexity.
   */
  const moreTabs = useMemo(() => {
    return tabs.slice(MAX_TABS_VISIBLE);
  }, [MAX_TABS_VISIBLE, tabs]);

  /**
   * MUI Tabs requires `value` to match a rendered Tab. Overflow
   * selections live only in the More menu, so represent them as More.
   */
  const isOverflowSelection = moreTabs.some(
    (tab) => tab.key === selectedTabKey,
  );
  const tabsValue = isOverflowSelection
    ? MORE_TAB_KEY
    : selectedTabKey || false;

  /**
   * Visible desktop Tabs already expose `${instanceId}-tab-${key}` ids.
   * Overflow items (and every item on the Select) do not, so keep a
   * visually hidden label mounted for those keys. That is the persistent
   * `aria-labelledby` target for the paired panel after the menu closes.
   */
  const tabsNeedingPersistentLabels = isMedium ? moreTabs : tabs;

  // More dropdown state
  const [moreAnchorEl, setMoreAnchorEl] = useState<null | HTMLElement>(null);
  const isMoreOpen = Boolean(moreAnchorEl);
  const moreMenuId = `${instanceId}-more-menu`;

  /**
   * More is a menu trigger. Only advertise controls that exist in the
   * DOM: the open menu, and the active overflow panel when one is selected.
   */
  const moreTabAriaControls =
    [
      isMoreOpen ? moreMenuId : undefined,
      isOverflowSelection && selectedTabKey
        ? tabPanelId(instanceId, selectedTabKey)
        : undefined,
    ]
      .filter(Boolean)
      .join(' ') || undefined;
  const closeMoreMenu = () => {
    setMoreAnchorEl(null);
  };

  const openMoreMenuFromTabs = (event: React.SyntheticEvent) => {
    setMoreAnchorEl(event.currentTarget as HTMLElement);
  };

  // A single tab has nothing to switch between, so skip the tab strip/select
  // entirely — the paired TabsToMobileSelectPanel still renders its content
  // since the effect above auto-selects that one tab.
  if (tabs.length <= 1) {
    return null;
  }

  return (
    <Box sx={{ my: 1 }}>
      {isMedium && tabs.length > 0 ? (
        <>
          <Tabs
            value={tabsValue}
            onChange={(_e, tabKey: string) => {
              const selected = tabs.find((tab) => tab.key === tabKey);
              if (selected) {
                handleTabChange(selected);
              }
            }}
            aria-label={ariaLabel}
          >
            {visibleTabs.map((tab) => {
              const isMoreTab = tab.key === MORE_TAB_KEY;

              return (
                <Tab
                  key={tab.key}
                  value={tab.key}
                  label={tab.label}
                  icon={tab.icon}
                  iconPosition='end'
                  id={tabElementId(instanceId, tab.key)}
                  aria-controls={
                    isMoreTab
                      ? moreTabAriaControls
                      : tabPanelId(instanceId, tab.key)
                  }
                  aria-haspopup={isMoreTab ? 'menu' : undefined}
                  aria-expanded={isMoreTab ? isMoreOpen : undefined}
                  onClick={isMoreTab ? openMoreMenuFromTabs : undefined}
                />
              );
            })}
          </Tabs>

          {moreTabs.length > 0 && (
            <Menu
              anchorEl={moreAnchorEl}
              open={isMoreOpen}
              onClose={closeMoreMenu}
              slotProps={{
                list: {
                  id: moreMenuId,
                  'aria-labelledby': tabElementId(instanceId, MORE_TAB_KEY),
                },
              }}
            >
              {moreTabs.map((tab) => (
                <MenuItem
                  key={tab.key}
                  onClick={() => {
                    handleTabChange(tab);
                    closeMoreMenu();
                  }}
                >
                  {tab.label}
                </MenuItem>
              ))}
            </Menu>
          )}
        </>
      ) : (
        <FormControl
          fullWidth={isMobile}
          size='small'
        >
          {ariaLabel && (
            <InputLabel
              id={`${instanceId}-select-label`}
              sx={visuallyHidden}
            >
              {ariaLabel}
            </InputLabel>
          )}
          <Select
            labelId={ariaLabel ? `${instanceId}-select-label` : undefined}
            aria-label={ariaLabel ? undefined : 'Select tab'}
            id={`${instanceId}-select`}
            value={selectedTabKey || ''}
            onChange={(event) => {
              const value = event.target.value;
              const selected = tabs.find((tab) => tab.key === value);
              if (selected) {
                handleTabChange(selected);
              }
            }}
            sx={(theme) => tabSelectDropdownStyling(theme)}
          >
            {tabs.map((tab) => (
              <MenuItem
                key={tab.key}
                value={tab.key}
              >
                {tab.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {tabsNeedingPersistentLabels.map((tab) => (
        <Box
          key={tab.key}
          component='span'
          id={tabElementId(instanceId, tab.key)}
          sx={visuallyHidden}
        >
          {tab.label}
        </Box>
      ))}
    </Box>
  );
};
